import { useState } from 'react'
import {
    NyuDataPointsWithParsedTime,
    NyuDataProperties,
    NyuGroupProperties,
    NyuSeriesProperties,
    NyuUiProperties,
} from '../../../shared/types'
import useGlobalState from '../hooks/useGlobalState'
import { cn } from '../utils/cn'
import { unwrapValueInArray } from '../utils/helpers'
import { CustomBarChart } from './CustomBarChart'
import { CustomLineChart } from './CustomLineChart'

const trackerURL = 'dhl.com/globalization'

const defaultChartHeight = 500
const minChartHeight = 300

export default function CustomChart({
    dataPoints,
    dataProperties,
    seriesProperties,
    uiProperties,
    groups,
}: {
    dataPoints: NyuDataPointsWithParsedTime
    dataProperties: NyuDataProperties
    seriesProperties: NyuSeriesProperties
    uiProperties: NyuUiProperties
    groups: NyuGroupProperties[]
}) {
    const verticalBarChart =
        dataProperties.chartType === 'bar' &&
        dataProperties.layout === 'vertical'

    const downloadView = useGlobalState((state) => state.downloadView)

    const source = unwrapValueInArray(dataProperties.source)
    const note = unwrapValueInArray(dataProperties.note)

    const [chartWidth, setChartWidth] = useState(0)

    const calculateChartHeightFromWidth = (chartWidth: number) => {
        if (chartWidth < 500 && !verticalBarChart) {
            return chartWidth * 0.9
        }

        const unit = uiProperties?.chartHeight?.unit || 'px'
        if (unit === 'px') {
            return uiProperties?.chartHeight?.value
        }

        const percentage = uiProperties?.chartHeight?.value || 30
        return (chartWidth * percentage) / 100
    }

    const calculateChartHeight = (chartWidth: number) =>
        Math.max(
            minChartHeight,
            calculateChartHeightFromWidth(chartWidth) || defaultChartHeight
        )

    const [chartHeight, setChartHeight] = useState(
        calculateChartHeight(chartWidth)
    )

    const handleResize = (nextChartWidth: number) => {
        setChartWidth(nextChartWidth)
        if (!verticalBarChart) {
            setChartHeight(calculateChartHeight(nextChartWidth))
        }
    }

    const Component =
        dataProperties.chartType === 'bar' ? CustomBarChart : CustomLineChart

    return (
        <div className="mt-4 bg-white">
            <Component
                dataPoints={dataPoints}
                dataProperties={dataProperties}
                seriesProperties={seriesProperties}
                groups={groups}
                chartWidth={chartWidth}
                chartHeight={chartHeight}
                onResize={handleResize}
                setChartHeight={setChartHeight}
            />

            <div
                className={cn(
                    'flex justify-between gap-2 flex-col md:flex-row items-start md:items-center'
                )}
            >
                <div className="space-y-2">
                    {source && (
                        <p>
                            <span className="font-bold">Data Source: </span>
                            {source}
                        </p>
                    )}
                    {note && (
                        <p>
                            <span className="font-bold">Note: </span>
                            {note}
                        </p>
                    )}
                </div>
                {downloadView && (
                    <div className="text-gray-500">{trackerURL}</div>
                )}
            </div>
        </div>
    )
}
